import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import authService from '../../services/authService';
import { useAuth } from '../../context/AuthContext';
import { useLocale } from '../../context/LocaleContext';

const CompleteProfile = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const { t, dir } = useLocale();

  const [checking, setChecking] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [guestInfo, setGuestInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    password: '',
    password_confirmation: '',
  });

  useEffect(() => {
    authService.getCompleteProfile(token)
      .then((res) => setGuestInfo(res.data.data))
      .catch(() => setInvalid(true))
      .finally(() => setChecking(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.password_confirmation) {
      toast.error('Passwords do not match!');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    try {
      setLoading(true);
      const res = await authService.completeProfile({ token, ...formData });

      // Sign the user in with the returned session
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      localStorage.setItem('userRole', 'user');
      updateUser(res.data.user);

      toast.success('Your account is ready!');
      // Full reload so AuthContext picks up the new token everywhere
      window.location.href = '/my-tickets';
    } catch (error) {
      const data = error.response?.data;
      if (data?.errors) {
        Object.values(data.errors).forEach(arr => arr.forEach(msg => toast.error(msg)));
      } else {
        toast.error(data?.message || 'Could not complete your profile.');
      }
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (checking) {
    return (
      <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center">
        <svg className="animate-spin h-10 w-10 text-purple-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  if (invalid) {
    return (
      <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-[420px]">
          <div className="auth-card px-7 py-9 sm:px-10 text-center">
            <h1 className="text-xl font-semibold text-gray-900">Link not valid</h1>
            <p className="mt-2 text-sm text-gray-500 leading-relaxed">
              This link is invalid or your profile was already completed.
            </p>
            <Link to="/login" className="inline-block mt-6 text-purple-700 font-medium hover:text-purple-900">
              {t('signIn')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-[480px]">
        <div className="auth-card px-7 py-9 sm:px-10">
          <h1 className="text-[1.75rem] font-medium text-gray-900 tracking-tight">Complete your account</h1>
          <p className="mt-2 text-sm text-gray-500 leading-relaxed">
            Set your name and password to access your tickets anytime.
          </p>

          {guestInfo && (
            <div className="mt-5 rounded-xl bg-purple-50 border border-purple-100 px-4 py-3 text-sm text-gray-700">
              <p><span className="font-medium">{t('email')}:</span> <span dir="ltr">{guestInfo.email}</span></p>
              {guestInfo.mobile && (
                <p className="mt-0.5"><span className="font-medium">{t('mobile')}:</span> <span dir="ltr">{guestInfo.mobile}</span></p>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="first_name" className={`block text-sm font-medium text-gray-700 mb-2 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{t('firstName')}</label>
                <input id="first_name" type="text" name="first_name" required autoComplete="given-name" dir={dir} placeholder={t('firstName')} value={formData.first_name} onChange={handleChange} className="auth-input auth-input-plain" />
              </div>
              <div>
                <label htmlFor="last_name" className={`block text-sm font-medium text-gray-700 mb-2 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{t('lastName')}</label>
                <input id="last_name" type="text" name="last_name" required autoComplete="family-name" dir={dir} placeholder={t('lastName')} value={formData.last_name} onChange={handleChange} className="auth-input auth-input-plain" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="password" className={`block text-sm font-medium text-gray-700 mb-2 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{t('password')}</label>
                <input id="password" type="password" name="password" required autoComplete="new-password" dir={dir} placeholder={t('phPasswordMin')} value={formData.password} onChange={handleChange} className="auth-input auth-input-plain" />
              </div>
              <div>
                <label htmlFor="password_confirmation" className={`block text-sm font-medium text-gray-700 mb-2 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{t('confirmPassword')}</label>
                <input id="password_confirmation" type="password" name="password_confirmation" required autoComplete="new-password" dir={dir} placeholder={t('phRepeatPassword')} value={formData.password_confirmation} onChange={handleChange} className="auth-input auth-input-plain" />
              </div>
            </div>

            <button type="submit" disabled={loading} className="auth-submit">
              {loading ? t('pleaseWait') : 'Complete My Account'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CompleteProfile;
